/* eslint-disable jsx-a11y/anchor-has-content, jsx-a11y/heading-has-content */

import { Link as RouterLink, LinkProps } from "@tanstack/react-router";
import React from "react";

import { cn } from "@/lib/utils";

export const Link = ({
  className,
  style,
  ...props
}: LinkProps & { className?: string; style?: React.CSSProperties }) => {
  return (
    <RouterLink
      className={cn(
        "font-medium text-slate-normal underline underline-offset-4",
        className,
      )}
      {...props}
      style={style}
    />
  );
};

Link.displayName = "Link";

export const Anchor = ({ className, ...props }: React.ComponentProps<"a">) => {
  return (
    <a
      className={cn(
        "font-medium text-blue-11 dark:text-bluedark-11 underline underline-offset-4",
        className,
      )}
      {...props}
    />
  );
};

Anchor.displayName = "Anchor";

// Make a re-usable component for each of the semantic elements
export const H1 = ({ className, ...props }: React.ComponentProps<"h1">) => {
  return (
    <h1
      className={cn(
        "scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-6xl instrument-serif",
        className,
      )}
      style={{
        lineHeight: "1.2",
      }}
      {...props}
    />
  );
};

H1.displayName = "H1";

export const H2 = ({ className, ...props }: React.ComponentProps<"h2">) => {
  return (
    <h2
      className={cn(
        "mt-10 scroll-m-20 border-b border-slate-7 dark:border-slatedark-7 pb-2 text-4xl font-semibold tracking-tight transition-colors first:mt-0",
        "instrument-serif",
        className,
      )}
      {...props}
    />
  );
};

H2.displayName = "H2";

export const H3 = ({ className, ...props }: React.ComponentProps<"h3">) => {
  return (
    <h3
      className={cn(
        "mt-6 scroll-m-20 text-2xl font-semibold tracking-tight",
        className,
      )}
      {...props}
    />
  );
};

H3.displayName = "H3";

export const H4 = ({ className, ...props }: React.ComponentProps<"h4">) => {
  return (
    <h4
      className={cn(
        "mt-6 scroll-m-20 text-lg font-semibold tracking-tight",
        className,
      )}
      {...props}
    />
  );
};

H4.displayName = "H4";

export const P = ({ className, ...props }: React.ComponentProps<"p">) => {
  return <p className={cn("leading-7", className)} {...props} />;
};

P.displayName = "P";

export const UnorderedList = ({
  className,
  ...props
}: React.ComponentProps<"ul">) => {
  return <ul className={cn("my-4 list-disc", className)} {...props} />;
};

UnorderedList.displayName = "UnorderedList";

export const OrderedList = ({
  className,
  ...props
}: React.ComponentProps<"ol">) => {
  return <ol className={cn("my-4 ml-2 list-decimal", className)} {...props} />;
};

OrderedList.displayName = "OrderedList";

export const ListItem = ({
  className,
  ...props
}: React.ComponentProps<"li">) => {
  return <li className={cn("my-2 ml-6 [&>li]:mt-2", className)} {...props} />;
};

ListItem.displayName = "ListItem";

export const Blockquote = ({
  className,
  ...props
}: React.ComponentProps<"blockquote">) => {
  return (
    <blockquote
      className={cn(
        "border-l-2 bg-slate-3 dark:bg-slatedark-3 pl-4 py-2 italic",
        className,
      )}
      {...props}
    />
  );
};

Blockquote.displayName = "Blockquote";

export const InlineCode = ({
  className,
  ...props
}: React.ComponentProps<"code">) => {
  return (
    <code
      className={cn(
        "bg-slate-2 dark:bg-slatedark-2 px-1.5 py-0.5 rounded text-sm font-medium",
        className,
      )}
      {...props}
    />
  );
};

InlineCode.displayName = "InlineCode";
